/* nimbus-motion-testimonials.js
 * NimbusTestimonials custom element — migrated to GSAP 3.
 * Preserves the original public API and DOM markup structure.
 *
 * Changes from nimbus-animations.js:
 *   • Desktop slide: GSAP tween on xPercent instead of inline transform
 *   • Progress bar: GSAP scaleX tween on the bar fill (replaces CSS animation)
 *   • Pause/resume: timeline.pause() / timeline.resume()
 *   • prefers-reduced-motion: disables autoplay + progress bar
 *   • Entrance animation: handled by nimbus-motion-modules.js 'testimonials'
 *
 * Public API preserved:
 *   el.go(index)       — jump to slide by index
 *   el.startAutoplay() — begin autoplay (if not reduced motion)
 *   el.stopAutoplay()  — stop autoplay
 */
(function () {
  'use strict';

  const reduced = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class NimbusTestimonials extends HTMLElement {
    connectedCallback() {
      this.layout      = this.dataset.layout || 'slider';
      this.autoplayOn  = this.dataset.autoplay !== 'false';
      this.delay       = parseInt(this.dataset.autoplayDelay, 10) || 5000;
      this.index       = 0;
      this._apTimer    = null;
      this._progressTl = null;

      if (this.layout !== 'slider') return;

      this.track   = this.querySelector('.nimbus-testimonials__track');
      this.dots    = Array.from(this.querySelectorAll('.nimbus-testimonials__dot'));
      this.prevBtn = this.querySelector('[data-dir="prev"]');
      this.nextBtn = this.querySelector('[data-dir="next"]');
      this.slides  = this.track
        ? Array.from(this.track.querySelectorAll('.nimbus-testimonial:not(.sb-carousel-clone)'))
        : [];

      if (!this.slides.length) return;

      this._buildProgressBar();
      this._bindControls();
      this._bindSwipe();
      this._bindHoverPause();

      this.update();

      if (this.autoplayOn && !reduced) this.startAutoplay();
    }

    isMobile() { return window.innerWidth <= 749; }

    /* ── Progress bar build ──────────────────────────────────────── */
    _buildProgressBar() {
      if (reduced) return;
      const controls = this.querySelector('.nimbus-testimonials__controls');
      if (!controls) return;

      const wrap = document.createElement('div');
      wrap.className = 'nimbus-testimonials__progress';
      const bar = document.createElement('div');
      bar.className = 'nimbus-testimonials__progress-bar';
      wrap.appendChild(bar);
      this._progressFill = bar;
      controls.parentNode.insertBefore(wrap, controls.nextSibling);
    }

    _startProgress() {
      if (!this._progressFill || reduced) return;
      if (this._progressTl) {
        this._progressTl.kill();
        this._progressTl = null;
      }
      gsap.set(this._progressFill, { scaleX: 0, transformOrigin: 'left center' });
      this._progressTl = gsap.to(this._progressFill, {
        scaleX: 1,
        duration: this.delay / 1000,
        ease: 'none'
      });
    }

    _stopProgress() {
      if (this._progressTl) {
        this._progressTl.kill();
        this._progressTl = null;
      }
      if (this._progressFill) {
        gsap.set(this._progressFill, { scaleX: 0 });
      }
    }

    /* ── Controls ────────────────────────────────────────────────── */
    _bindControls() {
      const go = (i) => {
        this.stopAutoplay();
        this.go(i);
        if (this.autoplayOn && !reduced) this.startAutoplay();
      };

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => go(this.index - 1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => go(this.index + 1));

      this.dots.forEach(dot =>
        dot.addEventListener('click', (e) => go(parseInt(e.currentTarget.dataset.index, 10)))
      );
    }

    /* ── Touch swipe (unchanged from original) ───────────────────── */
    _bindSwipe() {
      if (!this.track) return;
      let startX = 0, deltaX = 0;

      this.track.addEventListener('touchstart', e => {
        startX = e.touches[0].clientX;
        deltaX = 0;
      }, { passive: true });

      this.track.addEventListener('touchmove', e => {
        deltaX = e.touches[0].clientX - startX;
      }, { passive: true });

      this.track.addEventListener('touchend', () => {
        if (Math.abs(deltaX) > 40) {
          this.stopAutoplay();
          this.go(this.index + (deltaX < 0 ? 1 : -1));
          if (this.autoplayOn && !reduced) this.startAutoplay();
        }
      });
    }

    /* ── Hover / focus pause ─────────────────────────────────────── */
    _bindHoverPause() {
      if (!this.autoplayOn || reduced) return;

      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());
      this.addEventListener('focusin',    () => this.stopAutoplay());
      this.addEventListener('focusout',   () => this.startAutoplay());
    }

    /* ── go(i) — jump to a slide ─────────────────────────────────── */
    go(i) {
      const n    = this.slides.length;
      this.index = ((i % n) + n) % n;
      this.update();
    }

    /* ── update — sync DOM to this.index ─────────────────────────── */
    update() {
      if (this.isMobile()) {
        /* Mobile: CSS scroll-snap */
        const slide = this.slides[this.index];
        if (slide && this.track) {
          const tw = this.track.offsetWidth;
          const cx = slide.offsetLeft + slide.offsetWidth / 2 - tw / 2;
          this.track.scrollTo({ left: cx, behavior: 'smooth' });
        }
      } else {
        /* Desktop: GSAP xPercent tween */
        if (this.track) {
          gsap.to(this.track, {
            xPercent: -100 * this.index,
            duration: 0.6,
            ease: 'power3.out',
            overwrite: true
          });
        }
      }

      /* Dots */
      this.dots.forEach((d, i) => {
        if (i === this.index) d.setAttribute('aria-current', 'true');
        else d.removeAttribute('aria-current');
      });

      /* Slide aria */
      this.slides.forEach((slide, i) => {
        slide.setAttribute('aria-hidden', i !== this.index ? 'true' : 'false');
      });

      this._stopProgress();
      if (this._apTimer) this._startProgress();
    }

    /* ── Autoplay ─────────────────────────────────────────────────── */
    startAutoplay() {
      this.stopAutoplay();
      if (reduced) return;
      this._startProgress();
      this._apTimer = setInterval(() => this.go(this.index + 1), this.delay);
    }

    stopAutoplay() {
      clearInterval(this._apTimer);
      this._apTimer = null;
      this._stopProgress();
    }

    disconnectedCallback() {
      this.stopAutoplay();
    }
  }

  if (!customElements.get('nimbus-testimonials')) {
    customElements.define('nimbus-testimonials', NimbusTestimonials);
  }
})();
